import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Volume2, Star, Zap, Heart, Lightbulb } from 'lucide-react';
import { VocabGameProps } from '../types';
import { GAME_CONFIG } from '../config/constants';
import { playSound, calculateScore, calculateCoins, checkNewAchievements } from '../utils/helpers';
import { useTextToSpeech } from '../hooks/useTextToSpeech';
import { useVocabGame } from '../hooks/useVocabGame';
import LoadingScreen from './LoadingScreen';

const VocabGame = ({ gameState, saveGameState, preloadedVocab, onPreloadComplete }: VocabGameProps) => {
  // Game state
  const [input, setInput] = useState('');
  const [feedback, setFeedback] = useState<'none' | 'correct' | 'wrong' | 'missed'>('none');
  const [score, setScore] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [coins, setCoins] = useState(0);
  const [combo, setCombo] = useState(0);
  const [showBonusAnimation, setShowBonusAnimation] = useState(false);
  const [wrongAttempts, setWrongAttempts] = useState(0);
  const [showExample, setShowExample] = useState(false);
  const [waitingForNext, setWaitingForNext] = useState(false);

  // Custom hooks
  const { speak } = useTextToSpeech();
  const {
    words,
    currentWord,
    loading,
    timeLeft,
    isTimerActive,
    nextVocab,
    isGameComplete,
    progressPercentage,
    initializeGame,
    nextWord,
    startNewGame,
    pauseTimer,
    resumeTimer
  } = useVocabGame(preloadedVocab, onPreloadComplete);

  // Refs
  const inputRef = useRef<HTMLInputElement>(null);
  const actionButtonsRef = useRef<HTMLDivElement>(null);

  // Initialize game - only once on mount
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      initializeGame();
    }
  }, []);

  // Handle input focus for mobile keyboard
  const handleInputFocus = () => {
    setTimeout(() => {
      actionButtonsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
  };

  // Handle time up
  const handleTimeUp = useCallback(() => {
    pauseTimer();
    setFeedback('missed');
    playSound('wrong');

    const newHearts = Math.max(0, gameState.hearts - 1);
    saveGameState({ hearts: newHearts });

    if (newHearts === 0) {
      setGameOver(true);
      playSound('gameOver');
    } else {
      setTimeout(() => {
        moveToNext();
      }, 1500);
    }
  }, [pauseTimer, gameState.hearts, saveGameState]);

  // Check answer
  const checkAnswer = useCallback(() => {
    if (!currentWord || feedback !== 'none') return;

    const target = currentWord.english.toLowerCase().trim();
    const attempt = input.toLowerCase().trim();

    if (attempt === target) {
      // Calculate rewards
      const totalPoints = calculateScore(timeLeft, combo, GAME_CONFIG);
      const earnedCoins = calculateCoins(combo, GAME_CONFIG.COMBO_THRESHOLD);

      setScore(s => s + totalPoints);
      setCoins(c => c + earnedCoins);
      setCombo(c => c + 1);

      // Show bonus animation for combo
      if (combo >= GAME_CONFIG.COMBO_THRESHOLD) {
        setShowBonusAnimation(true);
        setTimeout(() => setShowBonusAnimation(false), 1000);
      }

      // Update game state
      saveGameState({
        totalScore: gameState.totalScore + totalPoints,
        streak: gameState.streak + 1,
        coins: gameState.coins + earnedCoins
      });

      // Check achievements
      const newAchievements = checkNewAchievements(gameState, totalPoints, combo, gameState.achievements);
      if (newAchievements.length > 0) {
        saveGameState({
          achievements: [...gameState.achievements, ...newAchievements]
        });
      }

      setFeedback('correct');
      setTimeout(() => setFeedback('none'), 1000);
      pauseTimer();
      playSound('correct');
      speak(`Excellent! ${currentWord.english}`);

      // Show example and wait for next
      setShowExample(true);
      setWaitingForNext(true);
    } else {
      // Wrong answer
      const newWrongAttempts = wrongAttempts + 1;
      setWrongAttempts(newWrongAttempts);
      setFeedback('wrong');
      setCombo(0);
      playSound('wrong');

      const hint = target.substring(0, Math.min(newWrongAttempts, target.length));
      speak(`Try again. It starts with ${hint}`);

      setTimeout(() => {
        setFeedback('none');
        setInput('');
      }, 1000);
    }
  }, [
    currentWord,
    feedback,
    input,
    timeLeft,
    combo,
    wrongAttempts,
    gameState,
    saveGameState,
    pauseTimer,
    speak
  ]);

  // Move to next word
  const moveToNext = useCallback(() => {
    setFeedback('none');
    setInput('');
    setShowHint(false);
    setWrongAttempts(0);
    setShowExample(false);
    setWaitingForNext(false);
    resumeTimer();

    if (!isGameComplete) {
      nextWord();
    } else {
      // Level complete
      saveGameState({
        level: gameState.level + 1,
        hearts: Math.min(5, gameState.hearts + 1)
      });

      setGameOver(true);
      playSound('levelUp');
      speak('Amazing! You completed the level!');
    }
  }, [
    isGameComplete,
    nextWord,
    resumeTimer,
    gameState.level,
    gameState.hearts,
    saveGameState,
    speak
  ]);

  // Handle timer reaching zero
  useEffect(() => {
    if (timeLeft === 0 && isTimerActive) {
      handleTimeUp();
    }
  }, [timeLeft, isTimerActive, handleTimeUp]);

  // Reset game
  const resetGame = useCallback(() => {
    setScore(0);
    setCoins(0);
    setCombo(0);
    setWrongAttempts(0);
    setInput('');
    setShowHint(false);
    setShowExample(false);
    setWaitingForNext(false);
    setFeedback('none');
    setGameOver(false);
  }, []);

  // Start new game
  const handleNewGame = useCallback(() => {
    resetGame();
    startNewGame(true);
  }, [resetGame, startNewGame]);

  if (loading) {
    return <LoadingScreen message="🔍 Finding fun words..." />;
  }

  if (gameOver) {
    const isWin = gameState.hearts > 0;
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 text-center space-y-6">
        <div className="text-8xl animate-bounce filter drop-shadow-lg">
          {isWin ? '🎉' : '💔'}
        </div>
        <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-purple-600">
          {isWin ? 'Amazing Level Up!' : 'Game Over'}
        </h2>

        <div className="bg-white/80 backdrop-blur rounded-2xl p-4 shadow-lg space-y-2">
          <div className="flex items-center justify-center space-x-4">
            <div className="flex items-center text-yellow-600">
              <Star className="w-6 h-6 fill-yellow-400" />
              <span className="font-bold ml-1">{gameState.totalScore + score}</span>
            </div>
            <div className="flex items-center text-orange-600">
              <Zap className="w-6 h-6" />
              <span className="font-bold ml-1">{gameState.streak} streak</span>
            </div>
            <div className="flex items-center text-purple-600">
              <Star className="w-6 h-6" />
              <span className="font-bold ml-1">Level {gameState.level}</span>
            </div>
          </div>
        </div>

        <div className="flex flex-col space-y-3">
          <button
            onClick={handleNewGame}
            className="bg-gradient-to-r from-green-400 to-emerald-500 text-white px-8 py-3 rounded-full font-bold text-xl shadow-lg hover:from-green-500 hover:to-emerald-600 transform hover:scale-105 transition-all relative"
          >
            {isWin ? (
              <>
                {nextVocab && nextVocab.length > 0 && (
                  <div className="absolute -top-2 -right-2 bg-yellow-400 text-yellow-900 text-xs px-2 py-1 rounded-full font-bold animate-pulse">
                    ⚡ Ready!
                  </div>
                )}
                🎮 Next Level
              </>
            ) : (
              '🔄 Try Again'
            )}
          </button>
          <button
            onClick={() => window.location.reload()}
            className="bg-gradient-to-r from-gray-400 to-gray-500 text-white px-6 py-2 rounded-full font-bold text-sm shadow-lg hover:from-gray-500 hover:to-gray-600 transform hover:scale-105 transition-all"
          >
            🏠 Main Menu
          </button>
        </div>
      </div>
    );
  }

  if (!currentWord) {
    return <LoadingScreen message="Loading word..." />;
  }

  return (
    <div className="flex flex-col items-center p-4 h-full relative overflow-y-auto">
      {/* Timer and Progress */}
      <div className="w-full mb-4 space-y-2">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
              timeLeft > 10 ? 'bg-green-500' : timeLeft > 5 ? 'bg-yellow-500' : 'bg-red-500 animate-pulse'
            }`}>
              {timeLeft}
            </div>
            <div className="flex items-center bg-purple-100 px-2 py-1 rounded-full">
              <Zap className="w-4 h-4 text-purple-600" />
              <span className="text-sm font-bold text-purple-600 ml-1">{combo}x</span>
            </div>
            <div className="flex items-center bg-yellow-100 px-2 py-1 rounded-full">
              <Star className="w-4 h-4 text-yellow-600" />
              <span className="text-sm font-bold text-yellow-600 ml-1">{gameState.coins + coins}</span>
            </div>
          </div>
          <div className="text-sm text-gray-600 font-medium">
            {words.findIndex(w => w.english === currentWord.english) + 1} / {words.length}
          </div>
        </div>

        <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
          <div
            className="bg-gradient-to-r from-blue-400 to-purple-500 h-4 rounded-full transition-all duration-500 relative"
            style={{ width: `${progressPercentage}%` }}
          >
            <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
          </div>
        </div>
      </div>

      {/* Game Card */}
      <div className="bg-white/90 backdrop-blur border-2 border-sky-100 w-full flex-1 rounded-3xl shadow-xl flex flex-col items-center justify-center p-6 mb-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-50 to-purple-50 opacity-50"></div>

        {/* Bonus Animation */}
        {showBonusAnimation && (
          <div className="absolute inset-0 flex items-center justify-center z-20">
            <div className="text-6xl font-bold text-yellow-500 animate-bounce filter drop-shadow-lg">
              ⚡ COMBO x{combo}!
            </div>
          </div>
        )}

        {/* Feedback Overlays */}
        {feedback === 'correct' && (
          <div className="absolute inset-0 flex items-center justify-center z-10 bg-green-500/20">
            <div className="text-8xl animate-ping filter drop-shadow-lg">✅</div>
          </div>
        )}

        {feedback === 'wrong' && (
          <div className="absolute inset-0 flex items-center justify-center z-10 bg-red-500/20">
            <div className="text-8xl animate-pulse filter drop-shadow-lg">❌</div>
          </div>
        )}

        {feedback === 'missed' && (
          <div className="absolute inset-0 flex items-center justify-center z-10 bg-orange-500/20">
            <div className="text-8xl animate-pulse filter drop-shadow-lg">⏰</div>
          </div>
        )}

        {/* Word Content */}
        <div className="relative z-5 text-center">
          <div className="text-8xl mb-4 animate-bounce-short filter drop-shadow-lg">{currentWord.emoji}</div>
          <h2 className="text-3xl font-bold text-gray-800 mb-4">{currentWord.chinese}</h2>

          {showHint && (
            <div className="bg-sky-100 border-2 border-sky-200 px-4 py-2 rounded-xl text-sky-700 mb-4 animate-pulse">
              💡 Hint: {currentWord.hint}
              <div className="text-xs text-sky-600 mt-1 font-medium">
                💬 中文提示: {currentWord.hintChinese || '请填入正确的英文单词'}
              </div>
            </div>
          )}

          {wrongAttempts > 0 && (
            <div className="bg-orange-100 border-2 border-orange-300 px-4 py-2 rounded-xl text-orange-700 mb-4 animate-bounce">
              <span className="font-bold">🔤 字母提示: </span>
              <span className="font-mono text-xl tracking-widest">
                {currentWord.english.substring(0, wrongAttempts)}
                {currentWord.english.length > wrongAttempts &&
                  <span className="text-orange-400">{'_'.repeat(currentWord.english.length - wrongAttempts)}</span>
                }
              </span>
              <div className="text-xs text-orange-600 mt-1">
                已答错 {wrongAttempts} 次，再接再厉！
              </div>
            </div>
          )}

          <div className="w-full max-w-xs">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onFocus={handleInputFocus}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && feedback === 'none') {
                  checkAnswer();
                }
              }}
              placeholder="Type English word..."
              className={`w-full text-center text-2xl p-4 border-2 rounded-xl outline-none transition-all duration-200 font-bold
                ${feedback === 'correct' ? 'border-green-500 bg-green-50 text-green-700 animate-pulse' : ''}
                ${feedback === 'wrong' ? 'border-red-400 bg-red-50 text-red-700 animate-shake' : ''}
                ${feedback === 'missed' ? 'border-orange-400 bg-orange-50 text-orange-700 animate-pulse' : ''}
                ${feedback === 'none' ? 'border-gray-300 focus:border-sky-400 focus:ring-2 focus:ring-sky-200' : ''}
              `}
              autoFocus
              disabled={feedback !== 'none' || waitingForNext}
            />
          </div>

          {showExample && currentWord.exampleSentence && (
            <div className="mt-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 px-4 py-3 rounded-xl text-left animate-fade-in max-w-sm">
              <div className="flex items-center mb-2">
                <span className="text-2xl mr-2">📖</span>
                <span className="font-bold text-green-700">例句 Example:</span>
              </div>
              <div className="text-lg text-green-800 font-medium mb-2">
                {currentWord.exampleSentence}
              </div>
              <div className="text-sm text-green-600 border-t border-green-200 pt-2">
                <span className="font-bold">中文翻译：</span>
                {currentWord.exampleSentenceChinese || '暂无翻译'}
              </div>
              <button
                onClick={() => speak(currentWord.exampleSentence || '')}
                className="mt-2 flex items-center text-green-600 hover:text-green-800 transition-colors"
              >
                <Volume2 size={18} className="mr-1" />
                <span className="text-sm">听例句发音</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      {waitingForNext ? (
        <div ref={actionButtonsRef} className="w-full pb-4">
          <button
            onClick={moveToNext}
            className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-bold py-4 px-6 rounded-xl shadow-lg transition-all transform hover:scale-105 active:translate-y-1 text-xl flex items-center justify-center space-x-2"
          >
            <span>下一题</span>
            <span className="text-2xl">👉</span>
          </button>
        </div>
      ) : (
        <div ref={actionButtonsRef} className="grid grid-cols-4 gap-2 w-full pb-4">
          <button
            onClick={() => {
              setShowHint(true);
              playSound('coin');
            }}
            disabled={showHint || feedback !== 'none'}
            className="col-span-1 bg-purple-100 hover:bg-purple-200 disabled:bg-gray-100 text-purple-600 disabled:text-gray-400 p-3 rounded-xl flex items-center justify-center transition-all transform hover:scale-105 disabled:scale-100 relative group"
          >
            <Lightbulb size={24} className="group-hover:animate-pulse" />
            <span className="absolute -top-1 -right-1 bg-purple-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">-1</span>
          </button>

          <button
            onClick={() => speak(currentWord.english)}
            className="col-span-1 bg-sky-100 hover:bg-sky-200 text-sky-600 p-3 rounded-xl flex items-center justify-center transition-all transform hover:scale-105"
          >
            <Volume2 size={24} />
          </button>

          <button
            onClick={checkAnswer}
            disabled={!input.trim() || feedback !== 'none'}
            className="col-span-2 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 disabled:from-gray-400 disabled:to-gray-500 text-white font-bold p-4 rounded-xl shadow-lg transition-all transform active:translate-y-1 disabled:transform-none disabled:cursor-not-allowed"
          >
            Check Answer ✓
          </button>
        </div>
      )}

      {/* Hearts Display */}
      <div className="flex items-center justify-center space-x-1 mt-2">
        {Array.from({ length: 5 }, (_, i) => (
          <Heart
            key={i}
            size={20}
            className={`${i < gameState.hearts ? 'text-red-500 fill-red-500 animate-pulse' : 'text-gray-300'} transition-all duration-300`}
          />
        ))}
      </div>
    </div>
  );
};

export default VocabGame;